import React, { CSSProperties } from "react";
import {
  DateRangePicker,
  FocusedInputShape,
  SingleDatePicker,
  isInclusivelyBeforeDay
} from "react-dates";
import { DatePickerProps } from "./types";
import Dropdown, { customStyles } from "../../Dropdown/Dropdown";
import { dropdownItem } from "../../Dropdown/types";
import * as moment from "moment";
import Moment from "moment";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import "./index.scss";

const START_DATE = "startDate";
const END_DATE = "endDate";

const defaultProps = {
  // input related props
  startDatePlaceholderText: "Date from:",
  endDatePlaceholderText: "Date to:",
  startDateId: START_DATE,
  endDateId: END_DATE,

  // calendar presentation and interaction related props
  numberOfMonths: 1,
  keepOpenOnDateSelect: true,
  readOnly: false,

  navNext: (
    <div tabIndex={0} className="month-nav month-nav--next">
      <span className="icon" />
    </div>
  ),
  navPrev: (
    <div tabIndex={0} className="month-nav month-nav--prev">
      <span className="icon" />
    </div>
  ),

  // day presentation and interaction related props
  enableOutsideDays: false,
  isOutsideRange: (day: any) => !isInclusivelyBeforeDay(day, Moment()),

  // internationalization
  displayFormat: "D MMM YYYY"
};

type State = {
  focusedState?: boolean;
  selectedDate?: Moment.Moment | null;
  focusedInput: FocusedInputShape | null;
  selectedYear: number;
};

let newStyles = Object.assign({}, customStyles);
newStyles["container"] = (provided: CSSProperties) => ({
  ...provided,
  textTransform: "uppercase" as "uppercase",
  fontWeight: 900,
  width: "calc(100% - 100px)",
  margin: "0 auto"
});

newStyles["control"] = (provided: CSSProperties) => ({
  ...provided,
  height: 38,
  cursor: "pointer",
  border: "solid 2px #54565a",
  borderRadius: 0,
  margin: 0
});

newStyles["placeholder"] = (provided: CSSProperties) => ({
  ...provided,
  color: "#54565a",
  margin: "0 auto",
  marginTop: 2,
  fontSize: 16,
  width: "90%",
  textAlign: "right"
});

newStyles["menu"] = (provided: CSSProperties) => ({
  ...provided,
  border: "solid 2px #54565a",
  borderRadius: 0,
  borderWidth: "0 2px 2px",
  margin: 0,
  fontWeight: 900,
  zIndex: 999
});

newStyles["menuList"] = (provided: CSSProperties) => ({
  ...provided,
  padding: 0,
  maxHeight: 180
});

const yearSelectionOpts = (selectedYear: number, startYear?: number) => {
  // get current year, go back to 1975
  const currentYear = new Date().getFullYear();
  let year = new Date().getFullYear() + 1;
  let yearSelectionOpts: dropdownItem[] = [];

  while (year > (startYear ? startYear : 1975)) {
    yearSelectionOpts.push({
      value: Moment().year() - (currentYear - year),
      label: year.toString(),
      disabled: selectedYear === year
    });
    year--;
  }

  return yearSelectionOpts;
};

class DateRangePickerWrapper extends React.Component<DatePickerProps, State> {
  constructor(props: DatePickerProps) {
    super(props);

    let focusedInput: FocusedInputShape | null = null;
    if (props.autoFocus) {
      focusedInput = START_DATE;
    } else if (props.autoFocusEndDate) {
      focusedInput = END_DATE;
    }

    this.state = {
      focusedInput,
      focusedState: false,
      selectedDate: null,
      selectedYear: Moment().year()
    };

    this.onDatesChange = this.onDatesChange.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);
  }

  static defaultProps = defaultProps;

  onDatesChange(props: any) {
    const {
      startDate = null,
      endDate = null,
      onDateUpdate,
      onDatesUpdate,
      isSingleDatePicker
    } = props;
    if (isSingleDatePicker) {
      if (onDateUpdate) onDateUpdate(props);
    } else {
      if (onDatesUpdate) onDatesUpdate(startDate, endDate);
    }

    if (this.state.focusedInput == "endDate") {
      this.setState({ focusedInput: null }, () => {
        if (this.props.endDateCallback !== undefined) {
          this.props.endDateCallback(this.props.endDate);
        }
      });
    }
  }

  onFocusChange(focusedInput: any) {
    this.setState({ focusedInput });
  }

  onMonthChange(params: moment.Moment) {
    this.setState({
      selectedYear: params.year()
    });
  }

  render() {
    const {
      focusedInput,
      selectedYear,
      focusedState = false,
      selectedDate = null
    } = this.state;
    const {
      enableOutsideDays,
      elementId,
      isOutsideRange,
      displayFormat,
      keepOpenOnDateSelect,
      numberOfMonths,
      startDateId,
      endDateId,
      navNext,
      navPrev,
      startDatePlaceholderText,
      endDatePlaceholderText,
      className,
      startDate = null,
      endDate = null,
      disabled,
      clearDateCallback,
      readOnly,
      isSingleDatePicker,
      onDatesUpdate,
      placeholder = "Date",
      required = false
    } = this.props;

    const initialDate = Moment(new Date()).subtract(13, "years");
    const days = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

    return (
      <div
        id={elementId}
        className={`date-picker${className ? " " + className : ""}${
          focusedInput ? " isFocused" + focusedInput : ""
        }`}
      >
        {isSingleDatePicker ? (
          <SingleDatePicker
            readOnly={readOnly}
            id={"datepicker--single"}
            initialVisibleMonth={() => initialDate}
            date={selectedDate}
            focused={focusedState}
            placeholder={placeholder}
            withPortal={true}
            displayFormat={displayFormat}
            required={required}
            onDateChange={selectedDate => {
              this.setState({
                selectedDate
              });
            }}
            onFocusChange={() => {
              this.setState({
                focusedState: !focusedState
              });
            }}
            numberOfMonths={1}
            enableOutsideDays={true}
            hideKeyboardShortcutsPanel={true}
            isOutsideRange={date => {
              return Moment(date).isAfter(initialDate);
            }}
            noBorder={true}
            renderMonthElement={({ month, onYearSelect }) => (
              <>
                <Dropdown
                  customStyles={newStyles}
                  options={yearSelectionOpts(selectedYear, 1924).filter(
                    year => year.value <= initialDate.get("year")
                  )}
                  className="dob-year-dd"
                  placeholder={`${month.format("MMMM")} ${month.format(
                    "YYYY"
                  )}`}
                  onChange={value => {
                    onYearSelect(month, value.label);
                    this.setState({
                      selectedYear: value.value as number
                    });
                  }}
                  menuPortalTarget={
                    document.querySelector(
                      `#${elementId} .DateRangePicker_picker`
                    ) as HTMLElement
                  }
                />
                <ul className="single-date-picker weekdays">
                  {days.map(day => (
                    <li key={day}>{day}</li>
                  ))}
                </ul>
                {/* <button
                  aria-label="close datepicker"
                  className="pop-up-close-btn"
                  onClick={() => {
                    this.setState({ focusedState: !focusedState });
                  }}
                >
                  close
                </button> */}
              </>
            )}
          />
        ) : (
          <DateRangePicker
            readOnly={readOnly}
            disabled={disabled}
            calendarInfoPosition={"top"}
            daySize={this.props.daySize}
            weekDayFormat={"ddd"}
            startDatePlaceholderText={startDatePlaceholderText}
            endDatePlaceholderText={endDatePlaceholderText}
            enableOutsideDays={enableOutsideDays}
            isOutsideRange={isOutsideRange}
            displayFormat={displayFormat}
            keepOpenOnDateSelect={keepOpenOnDateSelect}
            numberOfMonths={isSingleDatePicker ? 1 : numberOfMonths}
            onDatesChange={this.onDatesChange}
            onFocusChange={this.onFocusChange}
            onPrevMonthClick={this.onMonthChange.bind(this)}
            onNextMonthClick={this.onMonthChange.bind(this)}
            focusedInput={focusedInput}
            startDate={startDate}
            endDate={endDate}
            startDateId={startDateId}
            endDateId={endDateId}
            navNext={navNext}
            navPrev={navPrev}
            hideKeyboardShortcutsPanel={true}
            renderMonthElement={({ month, onYearSelect }) => (
              <Dropdown
                customStyles={newStyles}
                options={yearSelectionOpts(selectedYear)}
                placeholder={`${month.format("MMM")} ${month.format("YYYY")}`}
                onChange={value => {
                  onYearSelect(month, value.label);
                  this.setState({
                    selectedYear: value.value as number
                  });
                }}
                menuPortalTarget={
                  document.querySelector(
                    `#${elementId} .DateRangePicker_picker`
                  ) as HTMLElement
                }
              />
            )}
            renderCalendarInfo={() => (
              <>
                <button
                  aria-label="close datepicker"
                  className="pop-up-close-btn"
                  onClick={() => {
                    this.setState({ focusedInput: null });
                  }}
                />
                <button
                  onClick={() => {
                    if (onDatesUpdate) onDatesUpdate(null, null);
                    this.setState({ focusedInput: null }, () => {
                      if (clearDateCallback !== undefined) {
                        clearDateCallback();
                      }
                    });
                  }}
                  className="date-picker__clear-dates"
                >
                  Clear dates
                </button>
              </>
            )}
          />
        )}
      </div>
    );
  }
}

export default DateRangePickerWrapper;
